import {
  ArrowLeft,
  Search,
  BookOpen,
  FileText,
  Upload,
  Users,
  Shield,
  Settings,
  Play,
  ChevronRight,
  HelpCircle,
  Mail,
  Monitor,
  Zap,
  TrendingUp,
} from "lucide-react";
import Footer from "../../ui/Footer/Footer";
import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  categories as categoryData,
  allArticles,
  videos,
} from "./helpCenter/helpCenterData";
import styles from "./HelpCenter.module.css";

const iconMap = {
  "getting-started": <Upload size={22} />,
  "3d-2d-viewer": <Monitor size={22} />,
  collaboration: <Users size={22} />,
  "security-privacy": <Shield size={22} />,
  "account-billing": <Settings size={22} />,
  "integrations-api": <Zap size={22} />,
};

const popularArticles = allArticles
  .filter((a) => a.badge === "popular" || a.badge === "new")
  .slice(0, 7);

const HelpCenter = () => {
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const handleSearch = (e) => {
    e.preventDefault();
    if (search.trim()) {
      navigate(`/HelpCenter/search?q=${encodeURIComponent(search.trim())}`);
    }
  };

  return (
    <div className={styles.container}>
      {/* Hero */}
      <div className={styles.hero}>
        <div className={styles.heroInner}>
          <div className={styles.heroNav}>
            <Link to="/" className={styles.backLink}>
              <ArrowLeft size={16} /> Back to Home
            </Link>
          </div>
          <h1 className={styles.heroTitle}>Help Center</h1>
          <p className={styles.heroSubtitle}>
            Find answers, guides, and resources to help you get the most out of
            viewStruct.
          </p>
          <form onSubmit={handleSearch} className={styles.searchBox}>
            <Search size={18} className={styles.searchIcon} />
            <input
              type="text"
              placeholder="Search for articles, guides, or topics..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </form>
        </div>
      </div>

      {/* Tab Bar */}
      <div className={styles.tabBar}>
        <Link to="/contact" className={styles.tab}>
          Contact Us
        </Link>
        <button className={`${styles.tab} ${styles.tabActive}`}>
          Help Center
        </button>
        {/* <Link to="/issues" className={styles.tab}>
          Report an Issue
        </Link> */}
      </div>

      <div className={styles.mainContent}>
        {/* Categories */}
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>
            <BookOpen size={20} /> Browse by Category
          </h2>
        </div>
        <div className={styles.categoryGrid}>
          {categoryData.map((cat) => (
            <Link
              key={cat.slug}
              to={`/HelpCenter/category/${cat.slug}`}
              style={{ textDecoration: "none" }}
            >
              <div className={styles.categoryCard}>
                <div className={`${styles.categoryIcon} ${styles[cat.color]}`}>
                  {iconMap[cat.slug]}
                </div>
                <h3>{cat.title}</h3>
                <p>{cat.desc}</p>
                <span className={styles.articleCount}>
                  {cat.articles.length} articles →
                </span>
              </div>
            </Link>
          ))}
        </div>

        {/* Popular Articles */}
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>
            <TrendingUp size={20} /> Popular Articles
          </h2>
        </div>
        <div className={styles.articlesList}>
          {popularArticles.map((article) => (
            <div
              key={article.id}
              className={styles.articleItem}
              onClick={() => navigate(`/HelpCenter/article/${article.id}`)}
            >
              <div className={styles.articleInfo}>
                <div className={styles.articleIcon}>
                  <FileText size={14} />
                </div>
                <div className={styles.articleText}>
                  <h4>{article.title}</h4>
                  <span>{article.category}</span>
                </div>
              </div>
              <div className={styles.articleMeta}>
                {article.badge === "new" && (
                  <span className={`${styles.badge} ${styles.badgeNew}`}>
                    New
                  </span>
                )}
                {article.badge === "popular" && (
                  <span className={`${styles.badge} ${styles.badgePopular}`}>
                    Popular
                  </span>
                )}
                {article.badge === "updated" && (
                  <span className={`${styles.badge} ${styles.badgeUpdated}`}>
                    Updated
                  </span>
                )}
                <ChevronRight size={16} className={styles.articleArrow} />
              </div>
            </div>
          ))}
        </div>

        {/* Video Tutorials */}
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>
            <Play size={20} /> Video Tutorials
          </h2>
          {/* <Link to="/HelpCenter/videos" className={styles.viewAll}>
            View all <ChevronRight size={14} />
          </Link> */}
        </div>
        <div className={styles.videoGrid}>
          {videos.slice(0, 3).map((video) => (
            <Link
              key={video.id}
              to="/HelpCenter/videos"
              style={{ textDecoration: "none" }}
            >
              <div className={styles.videoCard}>
                <div className={styles.videoThumb}>
                  <iframe
                    width="365"
                    height="270"
                    src={video.url}
                    title={video.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                  ></iframe>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Contact CTA */}
        <div className={styles.contactCta}>
          <h3>Can't find what you're looking for?</h3>
          <p>Our support team is ready to help you with any questions.</p>
          <div className={styles.ctaLinks}>
            <Link
              to="/contact"
              className={`${styles.ctaLink} ${styles.ctaLinkPrimary}`}
            >
              <Mail size={16} /> Contact Support
            </Link>
            {/* <Link
              to="/issues"
              className={`${styles.ctaLink} ${styles.ctaLinkSecondary}`}
            >
              <HelpCircle size={16} /> Report an Issue
            </Link> */}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default HelpCenter;
